import type {
  Session,
  SessionDetail,
  AppSettings,
  KnowledgeFile,
  VocabItem,
  Exercise,
  ExerciseSummary,
  AttemptResult,
  SpeechAttemptResult,
  ReadingAttemptResult,
  WritingAttemptResult,
  PillarsResponse,
  GrammarProgress,
  FocusOverview,
  SessionMode,
  Usage,
  Me,
  Plan,
  BillingCycle,
  FeedbackCategory,
  FeedbackItem,
  FeedbackStatus,
  SessionCostMetric,
} from "../types";
import { checkoutReferral } from "../lib/referral";

// ── Auth token plumbing ──────────────────────────────────────────────────────
// Clerk's getToken is a React hook, but this module is plain functions. A
// top-level component registers the getter once (see ClerkTokenBridge); every
// request then attaches `Authorization: Bearer <token>`.
type TokenGetter = () => Promise<string | null>;
let _tokenGetter: TokenGetter | null = null;

export function setTokenGetter(fn: TokenGetter): void {
  _tokenGetter = fn;
}

export async function getAuthToken(): Promise<string | null> {
  try {
    return _tokenGetter ? await _tokenGetter() : null;
  } catch {
    return null;
  }
}

async function authHeaders(extra: Record<string, string> = {}): Promise<Record<string, string>> {
  const token = await getAuthToken();
  return token ? { ...extra, Authorization: `Bearer ${token}` } : extra;
}

/** Error carrying the HTTP status + server `detail` so callers can show paywall
 *  / quota messages (402) and capability errors (403) verbatim. */
export class ApiError extends Error {
  status: number;
  constructor(status: number, message: string) {
    super(message);
    this.status = status;
    this.name = "ApiError";
  }
}

async function errorFrom(res: Response): Promise<ApiError> {
  let detail = `${res.status} ${res.statusText}`;
  try {
    const body = await res.json();
    if (body?.detail) detail = body.detail;
  } catch {
    /* non-JSON body */
  }
  return new ApiError(res.status, detail);
}

async function request<T>(url: string, init?: RequestInit): Promise<T> {
  const method = (init?.method ?? "GET").toUpperCase();
  // Retry GET requests on network errors or 5xx (server restarting) with 1s delay.
  const maxRetries = method === "GET" ? 4 : 0;
  let lastError: unknown;

  for (let attempt = 0; attempt <= maxRetries; attempt++) {
    if (attempt > 0) await new Promise<void>(r => setTimeout(r, 1000));
    try {
      const headers = await authHeaders((init?.headers as Record<string, string>) || {});
      const res = await fetch(url, { ...init, headers });
      if (!res.ok) {
        if (res.status >= 500 && attempt < maxRetries) { lastError = await errorFrom(res); continue; }
        throw await errorFrom(res);
      }
      return res.json() as Promise<T>;
    } catch (e) {
      if (e instanceof TypeError && attempt < maxRetries) { lastError = e; continue; }
      throw e;
    }
  }
  throw lastError;
}

export const api = {
  createSession: (mode: SessionMode = "normal", focusKey?: string) =>
    request<{ session_id: number; session_number: number; mode: SessionMode; focus_label: string | null }>("/api/sessions", {
      method: "POST",
      headers: { "Content-Type": "application/json" },
      body: JSON.stringify({ mode, focus_key: focusKey ?? null }),
    }),

  getSessions: (limit = 50, offset = 0) =>
    request<Session[]>(`/api/sessions?limit=${limit}&offset=${offset}`),

  getSession: (id: number) =>
    request<SessionDetail>(`/api/sessions/${id}`),

  deleteSession: (id: number) =>
    request<{ deleted: number; refunded: boolean }>(`/api/sessions/${id}`, { method: "DELETE" }),

  /** Permanent account deletion. `confirmation` must be the user's own email —
   *  the server re-checks it and 400s on a mismatch. */
  deleteAccount: (confirmation: string) =>
    request<{ deleted: boolean }>("/api/me", {
      method: "DELETE",
      headers: { "Content-Type": "application/json" },
      body: JSON.stringify({ confirmation }),
    }),

  getSettings: () => request<AppSettings>("/api/settings"),

  updateSettings: (settings: AppSettings) =>
    request<AppSettings>("/api/settings", {
      method: "PUT",
      headers: { "Content-Type": "application/json" },
      body: JSON.stringify(settings),
    }),

  getKnowledge: () => request<KnowledgeFile[]>("/api/knowledge"),

  uploadKnowledge: async (file: File): Promise<{ filename: string; status: string }> => {
    const form = new FormData();
    form.append("file", file);
    const res = await fetch("/api/knowledge", { method: "POST", body: form, headers: await authHeaders() });
    if (!res.ok) throw await errorFrom(res);
    return res.json() as Promise<{ filename: string; status: string }>;
  },

  deleteKnowledge: (filename: string) =>
    request<{ deleted: string }>(`/api/knowledge/${encodeURIComponent(filename)}`, {
      method: "DELETE",
    }),

  addYouTube: (url: string) =>
    request<{ filename: string; status: string }>("/api/knowledge/youtube", {
      method: "POST",
      headers: { "Content-Type": "application/json" },
      body: JSON.stringify({ url }),
    }),

  getVocabulary: (sessionId?: number) =>
    request<VocabItem[]>(
      sessionId ? `/api/vocabulary?session_id=${sessionId}` : "/api/vocabulary"
    ),

  generateExercises: (sessionId?: number) =>
    request<{ status: "started" | "already_running" }>("/api/exercises/generate", {
      method: "POST",
      headers: { "Content-Type": "application/json" },
      body: JSON.stringify({ session_id: sessionId ?? null }),
    }),

  getGenerateStatus: () =>
    request<{ running: boolean; last_result: Record<string, number | boolean> | null }>(
      "/api/exercises/generate/status",
    ),

  getExercises: (params: { category?: string; sessionId?: number; status?: string } = {}) => {
    const q = new URLSearchParams();
    if (params.category) q.set("category", params.category);
    if (params.sessionId != null) q.set("session_id", String(params.sessionId));
    if (params.status) q.set("status", params.status);
    const qs = q.toString();
    return request<Exercise[]>(`/api/exercises${qs ? `?${qs}` : ""}`);
  },

  getExerciseSummary: (sessionId?: number) =>
    request<ExerciseSummary>(
      `/api/exercises/summary${sessionId != null ? `?session_id=${sessionId}` : ""}`,
    ),

  attemptExercise: (id: number, answer: string) =>
    request<AttemptResult>(`/api/exercises/${id}/attempt`, {
      method: "POST",
      headers: { "Content-Type": "application/json" },
      body: JSON.stringify({ answer }),
    }),

  attemptSpeech: async (id: number, audio: Blob): Promise<SpeechAttemptResult> => {
    const form = new FormData();
    form.append("audio", audio, "attempt.webm");
    const res = await fetch(`/api/exercises/${id}/attempt-speech`, { method: "POST", body: form, headers: await authHeaders() });
    if (!res.ok) throw await errorFrom(res);
    return res.json() as Promise<SpeechAttemptResult>;
  },

  generateReading: (sessionId?: number) =>
    request<{ created: number }>("/api/exercises/generate-reading", {
      method: "POST",
      headers: { "Content-Type": "application/json" },
      body: JSON.stringify({ session_id: sessionId ?? null }),
    }),

  generatePronunciation: (sessionId?: number) =>
    request<{ created: number }>("/api/exercises/generate-pronunciation", {
      method: "POST",
      headers: { "Content-Type": "application/json" },
      body: JSON.stringify({ session_id: sessionId ?? null }),
    }),

  generateWriting: (sessionId?: number) =>
    request<{ created: number }>("/api/exercises/generate-writing", {
      method: "POST",
      headers: { "Content-Type": "application/json" },
      body: JSON.stringify({ session_id: sessionId ?? null }),
    }),

  attemptReading: (id: number, answers: string[]) =>
    request<ReadingAttemptResult>(`/api/exercises/${id}/attempt-reading`, {
      method: "POST",
      headers: { "Content-Type": "application/json" },
      body: JSON.stringify({ answers }),
    }),

  attemptWriting: (id: number, answer: string) =>
    request<WritingAttemptResult>(`/api/exercises/${id}/attempt-writing`, {
      method: "POST",
      headers: { "Content-Type": "application/json" },
      body: JSON.stringify({ answer }),
    }),

  getPillars: () => request<PillarsResponse>("/api/stats/pillars"),

  getGrammarProgress: () => request<GrammarProgress>("/api/stats/grammar"),

  getFocus: () => request<FocusOverview>("/api/focus"),

  getUsage: () => request<Usage>("/api/usage"),

  getMe: () => request<Me>("/api/me"),

  completeOnboarding: (heardAbout?: string) =>
    request<{ onboarded: boolean }>("/api/me/onboarded", {
      method: "POST",
      headers: { "Content-Type": "application/json" },
      body: JSON.stringify({ heard_about: heardAbout?.trim() || null }),
    }),

  /** Mobile fallback: email a desktop link to someone blocked from a session. */
  requestMobileLink: (email: string, sourcePage: string) =>
    request<{ ok: boolean }>("/api/mobile-link", {
      method: "POST",
      headers: { "Content-Type": "application/json" },
      body: JSON.stringify({ email, source_page: sourcePage }),
    }),

  // ── Billing (Stripe) ───────────────────────────────────────────────────────
  // The referral is read here rather than passed in by the caller: every path to
  // checkout has to carry it, and a page that forgets is an unpaid partner.
  createCheckout: (plan: Exclude<Plan, "trial">, billing: BillingCycle) =>
    request<{ url: string }>("/api/billing/checkout", {
      method: "POST",
      headers: { "Content-Type": "application/json" },
      body: JSON.stringify({ plan, billing, referral: checkoutReferral() }),
    }),

  buySessionPack: (returnPath?: string) =>
    request<{ url: string }>("/api/billing/session-pack", {
      method: "POST",
      headers: { "Content-Type": "application/json" },
      body: JSON.stringify({ return_path: returnPath ?? null }),
    }),

  openBillingPortal: (returnPath?: string) =>
    request<{ url: string }>("/api/billing/portal", {
      method: "POST",
      headers: { "Content-Type": "application/json" },
      body: JSON.stringify({ return_path: returnPath ?? null }),
    }),

  deleteExercise: (id: number) =>
    request<{ deleted: number }>(`/api/exercises/${id}`, { method: "DELETE" }),

  sendFeedback: (payload: { category: FeedbackCategory; message: string; route?: string; session_id?: number }) =>
    request<{ id: number }>("/api/feedback", {
      method: "POST",
      headers: { "Content-Type": "application/json" },
      body: JSON.stringify(payload),
    }),

  // ── Admin: feedback triage ─────────────────────────────────────────────────
  getSessionCosts: (limit = 100) => request<SessionCostMetric[]>(`/api/admin/session-costs?limit=${limit}`),

  deleteSessionCost: (id: number) => request<{ deleted: number }>(`/api/admin/session-costs/${id}`, { method: "DELETE" }),

  getFeedback: () => request<FeedbackItem[]>("/api/feedback"),

  replyFeedback: (id: number, replyText: string) =>
    request<FeedbackItem>(`/api/feedback/${id}/reply`, {
      method: "POST",
      headers: { "Content-Type": "application/json" },
      body: JSON.stringify({ reply_text: replyText }),
    }),

  updateFeedbackStatus: (id: number, status: FeedbackStatus) =>
    request<FeedbackItem>(`/api/feedback/${id}/status`, {
      method: "PUT",
      headers: { "Content-Type": "application/json" },
      body: JSON.stringify({ status }),
    }),
};
